import { useCallback, useEffect, useState } from 'react'
import ReactPaginate from 'react-paginate'

import { PageLink, PageTitle } from '../../../../_metronic/layout/core'
import { EPayoutTicketStatus } from '../../../../types/enums'
import { Table } from '../../../../components/Table'
import { TableHead } from '../../../../components/Table/TableHead'
import { TableBody } from '../../../../components/Table/TableBody'
import { ROLE, useAuth } from '../../auth'
import { PAYOUT_REFUSALS_TAB } from '../refusals/core/consts'
import { IsGranted } from '../../auth/PrivateRoute'
import { getTicketsList } from '../api/getTicketsList'
import { PayoutTicketListObject, PreparePayoutTicketsToObject } from '../core/_models'

import TicketItem from './TicketItem'

import '../../../assets/css/pagination.css'
import { FilterManager, IBaseFilters } from '../../../common/filter/FilterManager'
import {
  ADAPTER_FILTER_LABEL,
  ADAPTER_ID_SEARCH_ERROR,
  adapterIdPayload,
  isAdapterScopeRequired,
  isAdapterSearchBlocked,
  usePaymentAdapterOptions,
} from '../../../common/filter/adapterIdSearch'
import { DEFAULT_ACTIVE_STATUS_GROUPS, ACTIVE_STATUS_GROUP_OPTIONS } from '../../../common/core/ticketStatusGroups'

const accountBreadCrumbs: Array<PageLink> = [
  {
    title: 'Активные',
    path: '/payout-tickets/active',
    isSeparator: false,
    isActive: true,
    roles: [
      ROLE.ROLE_ADMIN,
      ROLE.ROLE_MANAGER,
      ROLE.ROLE_SENIOR_CASHIER,
      ROLE.ROLE_CASHIER,
      ROLE.ROLE_SENIOR_OPERATOR,
      ROLE.ROLE_OPERATOR,
    ],
  },
  {
    title: 'Завершённые',
    path: '/payout-tickets/finished',
    isSeparator: false,
    isActive: false,
    roles: [
      ROLE.ROLE_ADMIN,
      ROLE.ROLE_MANAGER,
      ROLE.ROLE_SENIOR_CASHIER,
      ROLE.ROLE_CASHIER,
      ROLE.ROLE_SENIOR_OPERATOR,
      ROLE.ROLE_OPERATOR,
    ],
  },
  {
    title: 'Отчёт',
    path: '/payout-tickets/report',
    isSeparator: false,
    isActive: false,
    roles: [
      ROLE.ROLE_ADMIN,
      ROLE.ROLE_MANAGER,
      ROLE.ROLE_SENIOR_CASHIER,
      ROLE.ROLE_SENIOR_OPERATOR,
    ],
  },
  PAYOUT_REFUSALS_TAB,
]

const defaultTicketList: PayoutTicketListObject = {}
const perPage = 20

// Статусы по умолчанию для активных выплат (что показывается сейчас; без matching).
const defaultActiveStatuses = [
  EPayoutTicketStatus.ACTION_PENDING,
  EPayoutTicketStatus.CONFIRMATION_PENDING,
  EPayoutTicketStatus.PAYOUT_PENDING,
  EPayoutTicketStatus.PLACEMENT_PENDING,
]

// Исходные (дефолтные) значения фильтров для внутренних ролей — внутренние статусы.
const defaultFilterValues: IBaseFilters = {
  perPage,
  status: [...defaultActiveStatuses],
  searchAdvanced: { query: '', field: 'ticketToken' },
}

// Для мерчантских ролей (оператор/старший оператор) — бизнес-статусы (группы).
const defaultMerchantFilterValues: IBaseFilters = {
  perPage,
  status: [...DEFAULT_ACTIVE_STATUS_GROUPS],
  searchAdvanced: { query: '', field: 'ticketToken' },
}

const ActiveTickets = () => {
  const { currentUser } = useAuth()
  const [initialized, setInitialized] = useState(false)
  // Оператор/старший оператор — мерчантские роли: фильтруют по бизнес-статусам, а не по внутренним.
  // Бэкенд разворачивает группы только для сущности Operator с ROLE_OPERATOR. ROLE_PLATFORM (мерчант)
  // по иерархии тоже даёт ROLE_OPERATOR, но группы для него не разворачиваются — исключаем его здесь,
  // иначе платформа слала бы statusGroup[], который бэкенд игнорирует, и список остался бы без фильтра.
  const isMerchantRole =
    IsGranted([ROLE.ROLE_OPERATOR, ROLE.ROLE_SENIOR_OPERATOR], currentUser) &&
    !IsGranted([ROLE.ROLE_PLATFORM], currentUser)
  const activeDefaults = isMerchantRole ? defaultMerchantFilterValues : defaultFilterValues
  const [filters, setFilters] = useState<IBaseFilters>(activeDefaults)
  const [tickets, setTickets] = useState(defaultTicketList)
  const [countPages, setCountPages] = useState(0)
  const [fullCount, setFullCount] = useState(0)
  const [loading, setLoading] = useState(false)
  const adapterRequired = isAdapterScopeRequired(currentUser)
  const [searchError, setSearchError] = useState('')
  const adapterOptions = usePaymentAdapterOptions(adapterRequired)

  const getTicketList = useCallback(
    async (query: any) => {
      setLoading(true)
      try {
        const response = await getTicketsList(
          query
        )
        if (!response?.data) {
          throw new Error(
            'errors' in response?.data ? response?.data?.errors?.general_error : 'Unknown error'
          )
        }
        setTickets({ ...PreparePayoutTicketsToObject(response.data.tickets || {}) })
        setCountPages(Math.ceil(response.data.totalRows / perPage))
        setFullCount(response.data.totalRows || 0)
      } catch (error) {
        console.error('Ошибка при загрузке тикетов:', error)
        setTickets({})
        setCountPages(0)
        setFullCount(0)
      } finally {
        setLoading(false)
      }
    },
    []
  )

  const applyFilters = useCallback(
    (newFilters: IBaseFilters, pagination?: { page: number, perPage: number }) => {
      if (isAdapterSearchBlocked(adapterRequired, newFilters.searchAdvanced, 'paymentId', newFilters.paymentAdapterId)) {
        setSearchError(ADAPTER_ID_SEARCH_ERROR)
        return
      }
      setSearchError('')
      // Сохраняем применённые фильтры как источник истины, чтобы пагинация/обновление списка
      // не сбрасывали выбор статусов на дефолт.
      setFilters(newFilters)

      const query: Record<string, any> = {
        page: pagination?.page || 1,
        perPage: pagination?.perPage || perPage,
        // Мерчантские роли шлют бизнес-статусы (backend развернёт их во внутренние),
        // внутренние роли — как раньше, внутренние статусы.
        ...(isMerchantRole ? { statusGroup: newFilters.status } : { status: newFilters.status }),
      };

      if (newFilters.searchAdvanced?.query?.trim() && newFilters.searchAdvanced.field) {
        query[newFilters.searchAdvanced.field] = newFilters.searchAdvanced.query;
      }
      Object.assign(query, adapterIdPayload(adapterRequired, newFilters.searchAdvanced, 'paymentId', newFilters.paymentAdapterId));

      getTicketList(query)
    },
    [getTicketList, adapterRequired, isMerchantRole]
  )

  useEffect(() => {
    if (!initialized) {
      applyFilters(filters, { page: 1, perPage })
      setInitialized(true)
    }
  }, [initialized, applyFilters, filters, perPage])

  const ticketsRender = Object.keys(tickets).map((key) => (
    <TicketItem ticket={tickets[key]} typePage={'Active'} key={key} />
  ))

  return (
    <div>
      <div className='card sticky-top-tickets mb-7'>
        <PageTitle breadcrumbs={accountBreadCrumbs}>Активные</PageTitle>
        {/* begin::Header */}
        <div className='card-header border-0 pt-5 pb-2'>
          <h3 className='card-title align-items-start flex-column'>
            <span className='card-label fw-bold fs-3 mb-1'>Активные тикеты</span>
            <span className='text-muted mt-1 fw-semibold fs-7'>Количество: {fullCount}</span>
          </h3>
        </div>
        <FilterManager
          activeFilters={['searchAdvanced', { filter: 'select', fieldName: 'paymentAdapterId', visibility: adapterRequired }, {
            filter: 'selectMulti',
            fieldName: 'status',
            // Фильтр статуса — только оператору/старшему оператору (по тикету CPAYMENT-1589).
            visibility: isMerchantRole,
          }]}
          onApplyFilters={applyFilters}
          filterProps={{
            searchAdvanced: {
              options: [
                { label: 'ID тикета',     value: 'ticketToken' },
                { label: 'ID транзакции', value: 'merchantTransactionId' },
                { label: 'ID адаптера',   value: 'paymentId' },
              ],
              placeholder: 'Введите значение...',
              error: searchError,
            },
            paymentAdapterId: {
              label: ADAPTER_FILTER_LABEL,
              options: adapterOptions,
              placeholder: 'Не выбран',
            },
            status: {
              label: 'Статус',
              options: ACTIVE_STATUS_GROUP_OPTIONS,
              placeholder: 'Все статусы',
            },
          }}
          defaultValues={activeDefaults}
        />
      </div>

      <div className='card'>
        <div className='card-body py-3'>
          <Table>
            <TableHead>
              <tr>
                <th className='min-w-120px'>ID тикета</th>
                <th className='min-w-150px'>Дата создания</th>
                <th className='min-w-150px'>Тип</th>
                <th className='min-w-120px'>Сумма</th>
                <th className='min-w-120px'>Клиент</th>
                {!IsGranted([ROLE.ROLE_MATCHING_OPERATOR], currentUser) && (
                  <th className='min-w-150px'>Мерчант</th>
                )}
                {IsGranted([ROLE.ROLE_ADMIN, ROLE.ROLE_MANAGER, ROLE.ROLE_SENIOR_OPERATOR, ROLE.ROLE_OPERATOR, ROLE.ROLE_MERCHANT_PAYMENTS_OPERATOR, ROLE.ROLE_MERCHANT_MANAGER], currentUser) && (
                  <th className='min-w-80px'>Статус плат.адаптера</th>
                )}
                <th className='min-w-80px'>Платёжный адаптер</th>
                {IsGranted([ROLE.ROLE_ADMIN, ROLE.ROLE_MANAGER, ROLE.ROLE_SENIOR_OPERATOR, ROLE.ROLE_MERCHANT_PAYMENTS_OPERATOR, ROLE.ROLE_MERCHANT_MANAGER], currentUser) && (
                  <th className='min-w-100px'>Касса</th>
                )}
                <th className='min-w-150px'>Статус</th>
                <th className='min-w-50px'></th>
              </tr>
            </TableHead>
            <TableBody>
              {loading ? (
                <tr>
                  <td colSpan={IsGranted([ROLE.ROLE_ADMIN, ROLE.ROLE_MANAGER, ROLE.ROLE_MERCHANT_PAYMENTS_OPERATOR, ROLE.ROLE_MERCHANT_MANAGER], currentUser) ? 12: 9}>Загрузка ...</td>
                </tr>
              ) : (
                ticketsRender
              )}
            </TableBody>
          </Table>
        </div>

        <ReactPaginate
          activeClassName={'active-pagination '}
          breakClassName={'item-pagination break-me-pagination '}
          containerClassName={'pagination-pagination'}
          disabledClassName={'disabled-page-pagination'}
          nextClassName={'item-pagination ki-duotone ki-right fs-2x'}
          pageClassName={'item-pagination pagination-page-pagination '}
          previousClassName={'item-pagination ki-duotone ki-left fs-2x'}
          breakLabel='...'
          nextLabel=''
          onPageChange={(selected) =>
            applyFilters({ ...filters }, { page: selected.selected + 1, perPage })
          }
          pageRangeDisplayed={5}
          pageCount={countPages}
          previousLabel=''
          renderOnZeroPageCount={null}
        />
      </div>
    </div>
  )
}

export default ActiveTickets
